import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import API_BASE_URL from '../config/apiConfig.js';
import fetchApi, { getFileUrl } from '../utils/api.js';
import { usePermissionContext } from '../contexts/PermissionContext.js';
import SearchBar from '../components/ui/SearchBar';
import PageSkeleton from '../components/ui/PageSkeleton';
import DataTable from '../components/ui/DataTable';

function UserList({ onAddUser, onEditUser, onPermissions, onChat }) {
    const { hasPermission } = usePermissionContext();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const filterCompanyId = queryParams.get('companyId') || queryParams.get('company_id');
    const [currentPage, setCurrentPage] = useState(() => {
        const savedPage = sessionStorage.getItem('lastPage_UserList');
        return savedPage ? parseInt(savedPage) : 1;
    });
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [currentUserRole, setCurrentUserRole] = useState('USER');
    const [currentUserId, setCurrentUserId] = useState(null);

    const [exportType, setExportType] = useState('');
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [exportColumns, setExportColumns] = useState({
        userName: true,
        userEmail: true,
        userMobileNumber: true,
        role: true,
        companyName: true,
        userStatus: true
    });

    const columnLabels = {
        userName: 'User Name',
        userEmail: 'Email',
        userMobileNumber: 'Mobile Number',
        role: 'Role',
        companyName: 'Company Name',
        userStatus: 'Status'
    };

    const toggleExportColumn = (columnKey) => {
        setExportColumns(prev => ({
            ...prev,
            [columnKey]: !prev[columnKey]
        }));
    };

    const token = sessionStorage.getItem('token');

    const fetchUsers = async (search = '', page = 1, limit = 10) => {
        try {
            if (!search) setLoading(true);
            const storedUser = JSON.parse(sessionStorage.getItem('user'));

            if (storedUser) {
                setCurrentUserRole(storedUser.role || 'USER');
                setCurrentUserId(storedUser._id || storedUser.id || null);
            }

            if (!token) {
                setLoading(false);
                return;
            }

            const queryParams = new URLSearchParams();
            if (search) queryParams.append('search', search);
            queryParams.append('page', page);
            queryParams.append('limit', limit);
            if (filterCompanyId) {
                queryParams.append('companyId', filterCompanyId);
            }

            const data = await fetchApi(`/users?${queryParams.toString()}`);

            if (data.users && Array.isArray(data.users)) {
                setRows(data.users);
                setTotalEntries(data.total || data.users.length);
                const pages = data.pages || Math.ceil((data.total || data.users.length) / limit);
                setTotalPages(pages);
                if (page > pages && pages > 0) setCurrentPage(1);
            } else if (Array.isArray(data)) {
                setRows(data);
                setTotalEntries(data.length);
                const pages = Math.ceil(data.length / limit);
                setTotalPages(pages);
                if (page > pages && pages > 0) setCurrentPage(1);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        sessionStorage.setItem('lastPage_UserList', currentPage);
    }, [currentPage]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchUsers(searchQuery, currentPage, itemsPerPage);
        }, 200);
        return () => clearTimeout(delayDebounceFn);
    }, [searchQuery, currentPage, itemsPerPage, filterCompanyId]);

    const handleEdit = (user) => onEditUser && onEditUser(user);

    const handleDelete = (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'Do you want to delete this user?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await fetchApi(`/users/${id}`, { method: 'DELETE' });
                    Swal.fire('Deleted!', 'User has been deleted.', 'success');
                    fetchUsers(searchQuery, currentPage, itemsPerPage);
                } catch (error) {
                    console.error(error);
                }
            }
        });
    };

    const handleAdd = () => onAddUser && onAddUser();

    const handleStatusChange = async (user, newStatus) => {
        try {
            await fetchApi(`/users/${user._id || user.id}`, {
                method: 'PUT',
                body: JSON.stringify({ userStatus: parseInt(newStatus) })
            });
            toast.success('Status updated successfully');
            fetchUsers(searchQuery, currentPage, itemsPerPage);
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Failed to update status');
        }
    };

    const handlePermissions = (user) => {
        const currentUser = JSON.parse(sessionStorage.getItem('user'));
        if (!currentUser || currentUser.role === 'USER') {
            toast.error('You do not have access to permissions');
            return;
        }
        if (onPermissions) onPermissions(user);
    };

    const handleExcelDownload = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams();
            if (searchQuery) queryParams.append('search', searchQuery);
            queryParams.append('limit', '0'); // Get all users

            const result = await fetchApi(`/users?${queryParams.toString()}`);
            const usersToExport = result.users || (Array.isArray(result) ? result : []);

            const worksheetData = usersToExport.map((u, index) => {
                const row = { 'No.': index + 1 };
                if (exportColumns.userName) row['User Name'] = u.userName;
                if (exportColumns.userEmail) row['Email'] = u.userEmail;
                if (exportColumns.userMobileNumber) row['Mobile Number'] = u.userMobileNumber;
                if (exportColumns.role) row['Role'] = u.role;
                if (exportColumns.companyName) row['Company Name'] = u.Company?.companyName || u.companyId?.companyName || '';
                if (exportColumns.userStatus) row['Status'] = u.userStatus === 1 ? 'Active' : 'Inactive';
                return row;
            });

            const worksheet = XLSX.utils.json_to_sheet(worksheetData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');
            XLSX.writeFile(workbook, 'UserList.xlsx');
        } catch (error) {
            console.error("Excel download error:", error);
            toast.error("Failed to export data");
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = async () => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams();
            if (searchQuery) queryParams.append('search', searchQuery);
            queryParams.append('limit', '0');

            const result = await fetchApi(`/users?${queryParams.toString()}`);
            const usersToPrint = result.users || (Array.isArray(result) ? result : []);

            const printWindow = window.open('', '', 'height=600,width=800');
            printWindow.document.write('<html><head><title>User List</title>');
            printWindow.document.write('<style>');
            printWindow.document.write('body { font-family: Arial, sans-serif; margin: 20px; }');
            printWindow.document.write('h1 { text-align: center; color: #0b3a54; }');
            printWindow.document.write('table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }');
            printWindow.document.write('th, td { border: 1px solid #ddd; padding: 5px; text-align: left; }');
            printWindow.document.write('th { background-color: #f0fdf4; color: #0b3a54; font-weight: 600; }');
            printWindow.document.write('</style></head><body>');
            printWindow.document.write('<h1>User List</h1>');
            printWindow.document.write('<table><thead><tr><th>No.</th>');
            if (exportColumns.userName) printWindow.document.write('<th>User Name</th>');
            if (exportColumns.userEmail) printWindow.document.write('<th>Email</th>');
            if (exportColumns.userMobileNumber) printWindow.document.write('<th>Mobile</th>');
            if (exportColumns.role) printWindow.document.write('<th>Role</th>');
            if (exportColumns.companyName) printWindow.document.write('<th>Company</th>');
            if (exportColumns.userStatus) printWindow.document.write('<th>Status</th>');
            printWindow.document.write('</tr></thead><tbody>');

            usersToPrint.forEach((u, index) => {
                printWindow.document.write('<tr>');
                printWindow.document.write(`<td>${index + 1}</td>`);
                if (exportColumns.userName) printWindow.document.write(`<td>${u.userName || ''}</td>`);
                if (exportColumns.userEmail) printWindow.document.write(`<td>${u.userEmail || ''}</td>`);
                if (exportColumns.userMobileNumber) printWindow.document.write(`<td>${u.userMobileNumber || ''}</td>`);
                if (exportColumns.role) printWindow.document.write(`<td>${u.role || ''}</td>`);
                if (exportColumns.companyName) printWindow.document.write(`<td>${u.Company?.companyName || (typeof u.companyId === 'object' && u.companyId ? u.companyId?.companyName : 'Shree Samrudh')}</td>`);
                if (exportColumns.userStatus) printWindow.document.write(`<td>${u.userStatus === 1 ? 'Active' : 'Inactive'}</td>`);
                printWindow.document.write('</tr>');
            });

            printWindow.document.write('</tbody></table></body></html>');
            printWindow.document.close();
            printWindow.print();
        } catch (error) {
            console.error("Print error:", error);
            toast.error("Failed to print data");
        } finally {
            setLoading(false);
        }
    };

    const pagesArr = useMemo(() => {
        const out = [];
        const maxButtons = 5;
        let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
        let end = Math.min(totalPages, start + maxButtons - 1);
        start = Math.max(1, end - maxButtons + 1);
        for (let i = start; i <= end; i += 1) out.push(i);
        return out;
    }, [currentPage, totalPages]);

    const roleUpper = currentUserRole?.toUpperCase();
    const isCompanyAdmin = roleUpper === 'ADMIN' || roleUpper === 'COMPANY_ADMIN';
    const isSuperAdmin = roleUpper === 'SUPER_ADMIN';

    const canManage = isSuperAdmin || isCompanyAdmin || hasPermission('User', 'update');
    const canUpdateOrDelete = isSuperAdmin || isCompanyAdmin || hasPermission('User', 'update') || hasPermission('User', 'delete');

    if (loading) return <PageSkeleton />;

    const columns = [
        { key: 'userName', label: 'NAME' },
        ...(currentUserRole === 'SUPER_ADMIN' ? [{
            key: 'companyName',
            label: 'COMPANY NAME',
            render: (item) => item.Company?.companyName || (item.companyId && typeof item.companyId === 'object' ? item.companyId.companyName : (item.companyId ? 'Other Company' : 'Shree Samrudh'))
        }] : []),
        { key: 'userMobileNumber', label: 'MOBILE NUMBER' },
        { key: 'userEmail', label: 'EMAIL' },
        {
            key: 'userStatus',
            label: 'STATUS',
            render: (item) => canManage ? (
                <select
                    value={item.userStatus}
                    onChange={(e) => handleStatusChange(item, e.target.value)}
                    style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: item.userStatus === 1 ? '#dcfce7' : '#fee2e2',
                        color: item.userStatus === 1 ? '#166534' : '#991b1b',
                        fontWeight: '600',
                        fontSize: '12px',
                        outline: 'none',
                        cursor: 'pointer'
                    }}
                >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                </select>
            ) : (
                <span className={`status-indicator ${item.userStatus === 1 ? 'active' : 'inactive'}`}>
                    {item.userStatus === 1 ? 'Active' : 'Inactive'}
                </span>
            )
        },
        ...(canManage ? [{
            key: 'permissions',
            label: 'PERMISSIONS',
            render: (item) => (
                <span onClick={() => handlePermissions(item)} style={{ color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}>Permissions</span>
            )
        }] : []),
        { key: 'userBirthdayDate', label: 'BIRTHDAY DATE', render: (item) => item.userBirthdayDate || 'N/A' },
        {
            key: 'profile',
            label: 'PROFILE',
            render: (item) => (
                <div style={{ position: 'relative', width: '32px', height: '32px' }}>
                    {item.userProfile ? (
                        <img
                            src={getFileUrl(item.userProfile)}
                            alt="Profile"
                            style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                const placeholder = e.target.parentElement.querySelector('.profile-placeholder');
                                if (placeholder) placeholder.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div
                        className="profile-placeholder"
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: '#4f46e5',
                            color: '#fff',
                            display: item.userProfile ? 'none' : 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            fontWeight: '600'
                        }}
                    >
                        {item.userName?.charAt(0).toUpperCase()}
                    </div>
                </div>
            )
        },
        ...((canManage || onChat || canUpdateOrDelete) ? [{
            key: 'actions',
            label: 'ACTIONS',
            render: (item) => (
                <div className="list-page-action-icons">
                    {onChat && (currentUserRole === 'SUPER_ADMIN' ? item.role?.toUpperCase() === 'ADMIN' : (item._id || item.id) !== currentUserId) && (
                        <button type="button" className="list-page-icon-btn" title="Chat" onClick={() => onChat(item)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                            </svg>
                        </button>
                    )}
                    {hasPermission('User', 'update') && (
                        <button type="button" className="list-page-icon-btn" title="Edit" onClick={() => handleEdit(item)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                            </svg>
                        </button>
                    )}
                    {hasPermission('User', 'delete') && (
                        <button type="button" className="list-page-icon-btn" title="Delete" onClick={() => handleDelete(item._id)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" >
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                        </button>
                    )}
                </div>
            )
        }] : [])
    ];

    return (
        <div className="list-page-container">
            <div className="list-page-header">
                <h1 className="list-page-title">User List</h1>
                <div className="list-page-action-buttons">
                    {hasPermission('User', 'report') && (
                        <button className="list-page-btn list-page-btn-excel" onClick={() => { setExportType('excel'); setIsExportModalOpen(true); }}>EXCEL</button>
                    )}
                    {hasPermission('User', 'report') && (
                        <button className="list-page-btn list-page-btn-print" onClick={() => { setExportType('print'); setIsExportModalOpen(true); }}>PRINT</button>
                    )}
                    {hasPermission('User', 'add') && (
                        <button className="list-page-btn list-page-btn-add" onClick={handleAdd}>ADD USER</button>
                    )}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={rows}
                loading={loading}
                emptyMessage="No records found"
                page={currentPage}
                pageSize={itemsPerPage}
                totalEntries={totalEntries}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                onPageSizeChange={setItemsPerPage}
                searchBar={
                    <SearchBar value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }} placeholder="Search users..." />
                }
            />

            {isExportModalOpen && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                    <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', width: '500px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h2 style={{ margin: 0, color: '#1e293b', fontSize: '18px', fontWeight: 'bold' }}>Select Columns to {exportType === 'excel' ? 'Export' : 'Print'}</h2>
                            <button onClick={() => { const allKeys = Object.keys(columnLabels); const allChecked = allKeys.every(k => exportColumns[k]); const newExportColumns = { ...exportColumns }; allKeys.forEach(k => newExportColumns[k] = !allChecked); setExportColumns(newExportColumns); }} style={{ padding: '4px 8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', cursor: 'pointer' }}>{Object.keys(columnLabels).every(k => exportColumns[k]) ? 'Deselect All' : 'Select All'}</button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                            {Object.entries(columnLabels).map(([key, label]) => (
                                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#333', cursor: 'pointer', padding: '4px' }}>
                                    <input type="checkbox" checked={exportColumns[key]} onChange={() => toggleExportColumn(key)} style={{ accentColor: '#06b6d4', width: '16px', height: '16px' }} />
                                    {label}
                                </label>
                            ))}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', pt: '10px', borderTop: '1px solid #e2e8f0', marginTop: '10px', paddingTop: '15px' }}>
                            <button onClick={() => setIsExportModalOpen(false)} style={{ padding: '8px 16px', borderRadius: '4px', border: '1px solid #cbd5e1', backgroundColor: 'white', color: '#475569', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                            <button onClick={() => { if (exportType === 'excel') handleExcelDownload(); else handlePrint(); setIsExportModalOpen(false); }} style={{ padding: '8px 16px', borderRadius: '4px', border: 'none', backgroundColor: '#06b6d4', color: 'white', cursor: 'pointer', fontWeight: '600' }}>{exportType === 'excel' ? 'Export Excel' : 'Print'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UserList;
